import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { signOut, User } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../config/firebase';
import MagicLinkAuth from './MagicLinkAuth';
import DashboardMenu from './Auth/DashboardMenu';
import { useViewport } from '../hooks/useViewport';
import { Button, IconButton, Avatar, StatusPill, LoadingDots } from './ui';

interface AuthProps {
  user: User | null;
  authResolving?: boolean;
  userOrganization?: 'culturegenesis' | 'partner';
  sidebarVisible?: boolean;
  onToggleSidebar?: () => void;
  onShowHelp?: () => void;
  googleConnectComponent?: React.ReactNode;
}

const Auth: React.FC<AuthProps> = ({
  user,
  authResolving = false,
  userOrganization = 'culturegenesis',
  sidebarVisible,
  onToggleSidebar,
  onShowHelp,
  googleConnectComponent,
}) => {
  const { isMobile, isTablet } = useViewport();
  // The header-actions drawer has its own state — sidebarVisible drives only
  // the chat sidebar. Sharing one state opened both overlays at once on mobile.
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);
  const hasParentSidebar = !!onToggleSidebar;
  const showDrawer = isMobile && drawerOpen;
  const toggleSidebar = () => {
    setDrawerOpen(false);
    onToggleSidebar?.();
  };
  const toggleDrawer = () => {
    if (sidebarVisible) onToggleSidebar?.();
    setDrawerOpen((o) => !o);
  };
  const closeMobileMenu = () => setDrawerOpen(false);

  const [error, setError] = useState<string>('');
  const [showPartnerLogin, setShowPartnerLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = useCallback(async () => {
    try {
      await signOut(auth);
      setError('');
    } catch (err: any) {
      console.error('Sign-out error:', err);
    }
  }, []);

  const handleSignIn = () => {
    setError('');
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    window.location.href = `${backendUrl}/auth/google`;
  };

  // -----------------------------------------------------------------------
  // Auth-resolving splash — covers the brief gap between page mount and
  // Firebase restoring the persisted session. Without this, signed-in users
  // see a flash of the sign-in card on every reload and may click "Sign in"
  // before Firebase finishes restoring.
  // -----------------------------------------------------------------------

  if (authResolving && !user) {
    return (
      <div style={signInStyles.container}>
        <div style={signInStyles.card}>
          <img
            src="/assets/logo.svg"
            width={48}
            height={48}
            alt="Culture Genesis"
            style={{ marginBottom: 18, borderRadius: 0, maxWidth: 'unset', maxHeight: 'unset' }}
          />
          <LoadingDots />
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Sign-in screen
  // -----------------------------------------------------------------------

  if (!user) {
    return (
      <div style={signInStyles.container}>
        <div style={signInStyles.card}>
          <img
            src="/assets/logo.svg"
            width={56}
            height={56}
            alt="Culture Genesis"
            style={{ marginBottom: 18, borderRadius: 0, maxWidth: 'unset', maxHeight: 'unset' }}
          />
          <h1 style={signInStyles.title}>Culture Genius</h1>
          <p style={signInStyles.tagline}>The team's AI assistant — ask, search, and ship.</p>

          {error && (
            <div style={signInStyles.error}>
              {error}
            </div>
          )}

          {!showPartnerLogin ? (
            <>
              <button onClick={handleSignIn} style={signInStyles.googleBtn}>
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.08-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.86 2.69-6.62z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.46-.81 5.95-2.18l-2.9-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A8.996 8.996 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3-2.33z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.997 8.997 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </button>

              <div style={signInStyles.divider}>
                <span style={signInStyles.dividerLine} />
                <span style={signInStyles.dividerText}>or</span>
                <span style={signInStyles.dividerLine} />
              </div>

              <Button
                variant="partner"
                size="lg"
                fullWidth
                onClick={() => setShowPartnerLogin(true)}
              >
                ✦ Partner sign-in (magic link)
              </Button>

              <p style={signInStyles.footer}>
                Culture Genesis team members sign in with Google.<br />
                Partners receive a magic link via email.
              </p>
            </>
          ) : (
            <>
              <div style={{ width: '100%' }}>
                <MagicLinkAuth
                  onSuccess={() => setError('')}
                  onError={(err) => setError(err)}
                />
              </div>

              <div style={signInStyles.divider}>
                <span style={signInStyles.dividerLine} />
                <span style={signInStyles.dividerText}>or</span>
                <span style={signInStyles.dividerLine} />
              </div>

              <Button
                variant="ghost"
                size="lg"
                fullWidth
                onClick={() => {
                  setShowPartnerLogin(false);
                  setError('');
                }}
              >
                ← Back to Google sign in
              </Button>

              <p style={signInStyles.footer}>
                Culture Genesis Partnerships team members only.
              </p>
            </>
          )}
        </div>

        <div style={signInStyles.legal}>
          © {new Date().getFullYear()} Culture Genesis
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Authenticated header chrome
  // -----------------------------------------------------------------------

  const firstName = (user.displayName || user.email || '').split(' ')[0]?.split('@')[0];

  return (
    <div style={{ ...headerStyles.header, flexWrap: isTablet ? 'nowrap' : 'wrap' }}>
      <div style={headerStyles.headerLeft}>
        {hasParentSidebar && (
          <IconButton
            label={sidebarVisible ? 'Close chat sidebar' : 'Open chat sidebar'}
            onClick={toggleSidebar}
            variant={sidebarVisible ? 'solid' : 'subtle'}
            size="lg"
            style={isMobile ? { width: 44, height: 44 } : undefined}
          >
            {sidebarVisible ? '✕' : '☰'}
          </IconButton>
        )}

        <div style={headerStyles.brandingContainer}>
          <img
            src="/assets/logo.svg"
            alt="Culture Genesis"
            style={headerStyles.logo}
          />
          <div style={headerStyles.branding}>
            <span style={headerStyles.appName}>Culture Genius</span>
            <span style={headerStyles.companyName}>Culture Genesis</span>
          </div>
        </div>
      </div>

      <div style={headerStyles.headerRight}>
        {!isMobile && googleConnectComponent}
        {!isMobile && (
          <Button
            variant={location.pathname === '/workflow' ? 'subtle' : 'spark'}
            size="sm"
            onClick={() => navigate('/workflow')}
          >
            {location.pathname === '/workflow' ? '← Back to Chat' : '+ Request workflow'}
          </Button>
        )}
        {!isMobile && <DashboardMenu />}
        {/* Avatar/name pill hidden on tablets so the header fits one row in
            phone landscape; the Partner badge stays visible on its own */}
        {!isMobile && !isTablet && (
          <div style={headerStyles.userBlock}>
            <Avatar
              name={user.displayName || undefined}
              email={user.email || undefined}
              src={user.photoURL || undefined}
              size={32}
            />
            <div style={headerStyles.userBlockText}>
              <span style={headerStyles.userName}>{firstName}</span>
              {userOrganization === 'partner' && (
                <StatusPill variant="partner" size="sm" style={{ marginTop: 2 }}>
                  Partner
                </StatusPill>
              )}
            </div>
          </div>
        )}
        {!isMobile && isTablet && userOrganization === 'partner' && (
          <StatusPill variant="partner" size="sm">
            Partner
          </StatusPill>
        )}
        {!isMobile && (
          <Button variant="ghost" size="sm" onClick={handleSignOut}>
            Sign out
          </Button>
        )}
        {isMobile && (
          <IconButton
            label={drawerOpen ? 'Close menu' : 'More options'}
            onClick={toggleDrawer}
            variant={drawerOpen ? 'solid' : 'subtle'}
            size="lg"
            style={{ width: 44, height: 44 }}
          >
            {drawerOpen ? '✕' : '⋯'}
          </IconButton>
        )}
      </div>

      {showDrawer && (
        <>
        {/* Portaled to body: inside the header, its backdrop-filter would trap
            this fixed element (inset resolves against the header box in
            Chromium) and it would paint over the header's own buttons. From
            the root context it sits below the header (--z-overlay), keeping
            the hamburger/kebab tappable while the drawer is open. */}
        {createPortal(
          <div
            style={headerStyles.mobileDrawerBackdrop}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />,
          document.body
        )}
        <div className="header-drawer" style={headerStyles.mobileDrawer}>
          <div style={headerStyles.mobileSection}>
            <div style={headerStyles.mobileSectionLabel}>Dashboards</div>
            <DashboardMenu inline onNavigate={closeMobileMenu} />
          </div>
          <div style={headerStyles.mobileSection}>
            <div style={headerStyles.mobileSectionLabel}>Actions</div>
            <Button
              variant="ghost"
              size="md"
              fullWidth
              onClick={() => {
                navigate('/workflow');
                closeMobileMenu();
              }}
              style={{ justifyContent: 'flex-start' }}
            >
              + Request workflow
            </Button>
            {googleConnectComponent && (
              <div style={headerStyles.mobileGoogleConnect}>{googleConnectComponent}</div>
            )}
            {onShowHelp && (
              <Button
                variant="ghost"
                size="md"
                fullWidth
                onClick={() => {
                  onShowHelp();
                  closeMobileMenu();
                }}
                style={{ justifyContent: 'flex-start' }}
              >
                ? Search tips
              </Button>
            )}
            <Button
              variant="ghost"
              size="md"
              fullWidth
              onClick={handleSignOut}
              style={{ justifyContent: 'flex-start' }}
            >
              Sign out
            </Button>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

// =====================================================================
// Sign-in styles — glassy card on gradient backdrop
// =====================================================================

const signInStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'var(--cg-gradient-bg)',
    padding: 24,
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: 'rgba(255,255,255,0.78)',
    backdropFilter: 'blur(24px) saturate(150%)',
    WebkitBackdropFilter: 'blur(24px) saturate(150%)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-2xl)',
    padding: '36px 32px',
    boxShadow: 'var(--shadow-xl)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    fontFamily: 'var(--font-sans)',
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: '-0.022em',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
  },
  tagline: {
    margin: '6px 0 24px',
    fontSize: 14,
    color: 'var(--fg-3)',
    lineHeight: 1.5,
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    padding: '12px 16px',
    background: '#fff',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'var(--shadow-xs)',
    transition: 'all 200ms var(--ease-standard)',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    margin: '18px 0',
  },
  dividerLine: { flex: 1, height: 1, background: 'var(--border-2)' },
  dividerText: {
    fontSize: 11,
    color: 'var(--fg-4)',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  footer: {
    fontSize: 12,
    color: 'var(--fg-4)',
    marginTop: 22,
    lineHeight: 1.55,
  },
  error: {
    width: '100%',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 500,
    textAlign: 'left',
    border: '1px solid var(--cg-terra-200)',
  },
  legal: {
    fontSize: 11.5,
    color: 'var(--fg-4)',
    marginTop: 24,
  },
};

// =====================================================================
// Authenticated header styles
// =====================================================================

const headerStyles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 18px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-glass)',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    flexShrink: 0,
    gap: 12,
    position: 'relative',
    zIndex: 'var(--z-overlay)' as React.CSSProperties['zIndex'],
    // flexWrap is set per-render at the JSX site: nowrap at ≤1024px so the
    // header stays a single --header-height row (the mobile sidebar offset
    // and drawer max-height depend on that).
    minHeight: 'var(--header-height)',
    fontFamily: 'var(--font-sans)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  brandingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logo: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: 0,
    maxWidth: 'unset',
    maxHeight: 'unset',
  },
  branding: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    lineHeight: 1.15,
  },
  appName: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--fg-1)',
    letterSpacing: '-0.01em',
  },
  companyName: {
    fontSize: 10.5,
    fontWeight: 600,
    color: 'var(--fg-4)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  userBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 10px 4px 4px',
    background: 'rgba(255,255,255,0.55)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-pill)',
  },
  userBlockText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--fg-1)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 120,
  },
  mobileDrawerBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'var(--bg-overlay)',
    zIndex: 'calc(var(--z-drawer) - 1)' as React.CSSProperties['zIndex'],
  },
  mobileDrawer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: 'rgba(255,255,255,0.96)',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    borderBottom: '1px solid var(--border-1)',
    boxShadow: 'var(--shadow-lg)',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    // max-height comes from the .header-drawer class (vh→dvh fallback pair)
    overflowY: 'auto',
    zIndex: 'var(--z-drawer)' as React.CSSProperties['zIndex'],
  },
  mobileSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  mobileSectionLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--fg-4)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    paddingLeft: 4,
  },
  mobileGoogleConnect: {
    padding: '4px 0',
  },
};

export default Auth;
